import { BaseViewStore } from 'core/stores';
import { FulfillmentOrderStore } from 'modules/order/stores';
import { ProductInventoryStore } from 'modules/product-inventory/stores';
import { ChangeShippingForm } from 'modules/order/forms';
import { ApplicationStore } from 'platform/application/stores';
import {
	notificationError,
	isSome,
	getDistinctProductIds,
	isSuperAdminOrganization,
	mapCarrierServiceWithRates,
	translate,
	shouldUseBuyShipping,
	getBulkItemsSellerId,
} from 'core/utils';
import { observable, action, makeObservable } from 'mobx';
import { CarrierServiceStore } from 'modules/carrier/stores';
import { RateServiceStore } from 'modules/rate/stores';
import { ShipmentInfoStore } from 'modules/shipment-info/stores';
import { ConfirmModalParams } from 'core/models';
import { channelType, onChangeShippingStatuses, sourceOfChange } from 'common/constants';
import _ from 'lodash';

class ChangeShippingViewStore extends BaseViewStore {
	@observable useBuyShipping = false;
	@observable ignoreDeliverByDate = true;
	@observable disableSaveBtn = false;
	@observable carrierServiceOptions = [];
	@observable tempCarrierServiceOptions = [];
	@observable pageCarrierService = 1;
	@observable warehouseOptions = [];
	@observable pageWarehouse = 1;
	@observable responseArray = [];
	@observable isCarrierServiceLoading = false;
	@observable infiniteSearchPhrase = null;
	@observable isWarehouseDropdownLoading = false;
	activeShipment = null;
	isBulkAction = false;
	isAllAmazon = false;
	currentCheckEvt = null;
	debouncedOnCheck = null;
	selectedItem = null;
	debouncedWarehouseSearch = null;
	debouncedCarrierServiceSearch = null;

	form = new ChangeShippingForm({
		onSuccess: () => {
			if (this.selectedItems != null) {
				this.changeShippingBulk;
			} else {
				this.changeShipping;
			}
		},
	});

	voidActiveLabelConfirmModal = new ConfirmModalParams({
		onConfirm: async () => {
			if (this.selectedItems == null) {
				await this.changeShipping(1);
			} else {
				await this.changeShippingBulk(1);
			}
		},
		onCancel: async () => {
			if (this.selectedItems == null) {
				await this.changeShipping(0);
			} else {
				await this.changeShippingBulk(0);
			}
		},
	});

	constructor(rootStore, { parentViewStore, selectedItemId, selectedItemIds }) {
		super(rootStore);
		makeObservable(this);
		this.parentView = parentViewStore;
		this.fulfillmentOrderStore = new FulfillmentOrderStore(rootStore);
		this.productInventoryStore = new ProductInventoryStore(rootStore);
		this.carrierServiceStore = new CarrierServiceStore(rootStore);
		this.rateServiceStore = new RateServiceStore(rootStore);
		this.shipmentInfoStore = new ShipmentInfoStore(rootStore);
		this.isSuperAdminOrganization = isSuperAdminOrganization(rootStore);
		this.applicationStore = new ApplicationStore(rootStore);
		this.debouncedOnCheck = _.debounce(async () => {
			this.useBuyShipping = this.form.$('useBuyShipping').value;
			await this.getCarrierServices(this.pageCarrierService);
		}, 500);
		this.debouncedWarehouseSearch = _.debounce((searchPhrase, { action }) => {
			this.searchWarehouseAsync(searchPhrase, { action });
		}, 500);
		this.debouncedCarrierServiceSearch = _.debounce((searchPhrase, { action }) => {
			this.searchCarrierServiceAsync(searchPhrase, { action });
		}, 500);
		this.initialize(selectedItemId, selectedItemIds);
	}

	@action.bound
	initialize = async (selectedItemId, selectedItemIds) => {
		this.suspend();
		//this is temporary
		if (selectedItemIds != null) {
			await this.findFulfillmentOrder(selectedItemIds);
			this.filteredItems = _.filter(this.selectedItems, selectedItem => {
				let index = _.findIndex(onChangeShippingStatuses, item => item == selectedItem.fulfillmentOrderStatus.abrv);
				if (index != -1) {
					return selectedItem;
				}
			});
			this.isBulkAction = true;
			this.sellerId = getBulkItemsSellerId(this.selectedItems);
			this.selectedItem = null;
			this.isAllAmazon =
				_.find(this.filteredItems, item => item.order.channel.channelType.name !== channelType.amazon) === undefined
					? true
					: false;
			this.useBuyShipping = false;
			this.form.$('useBuyShipping').value = this.useBuyShipping;
			this.ignoreDeliverByDate = this.isAllAmazon ? !this.useBuyShipping : true;
			this.form.$('ignoreDeliverByDate').value = this.ignoreDeliverByDate;
			this.form.$('assignedCarrierService').set('value', null);
		} else {
			await this.getFulfillmentOrder(selectedItemId);
			this.form.$('assignedCarrierService').set('rules', 'required');
			this.form.$('assignedCarrierService').value = {
				label: this.selectedItem.carrierService.name,
				value: {
					carrierServiceId: this.selectedItem.carrierService.id,
					carrierId: this.selectedItem.carrier != null ? this.selectedItem.carrier.id : null,
				},
				extra: this.selectedItem.carrierService,
			};
			this.useBuyShipping = shouldUseBuyShipping(this.selectedItem, this.form);
			this.ignoreDeliverByDate =
				this.selectedItem.order.channel.channelType.name === channelType.amazon ? !this.useBuyShipping : true;
			this.form.$('ignoreDeliverByDate').value = this.ignoreDeliverByDate;
		}
		this.form.state.options.set({
			validateOnBlur: false,
			showErrorsOnReset: false,
			validateOnChange: false,
		});
		await this.getWarehouseOptions();
		await this.getCarrierServices(this.pageCarrierService, true, null, true);
		this.resume();
	};

	@action.bound
	findFulfillmentOrder = async selectedItemIds => {
		this.selectedItems = (await this.fulfillmentOrderStore.findFulfillmentOrder({
			page: 1,
			rpp: 100,
			ids: selectedItemIds,
			embed: [
				'order',
				'order.channel',
				'order.channel.channelType',
				'fulfillmentOrderStatus',
				'fulfillmentOrderItems',
				'fulfillmentOrderItems.orderLine',
				'fulfillmentOrderItems.product',
				'shipmentInfos',
				'shipmentInfos.shipmentInfoStatus',
			],
		})).item;
	};

	@action.bound
	getFulfillmentOrder = async selectedItemId => {
		this.selectedItem = await this.fulfillmentOrderStore.getFulfillmentOrder(selectedItemId, {
			embed: [
				'order',
				'order.channel',
				'order.channel.channelType',
				'order.shippingCoreCountry',
				'order.shippingCoreCountryState',
				'carrierService',
				'carrier',
				'fulfillmentOrderItems',
				'fulfillmentOrderItems.product',
				'warehouseOrganization',
				'shipmentInfos',
				'shipmentInfos.shipmentInfoStatus',
			],
		});

		this.activeShipment = _.find(this.selectedItem.shipmentInfos, item => {
			return item.shipmentInfoStatus.abrv == 'active';
		});
	};

	composeWarehouseFilter(page, search) {
		let filter = {
			rpp: 15,
			page: page,
			isOrganizationActive: true,
			warehouseOnly: true,
		};
		if (this.sellerId) {
			filter.includeOnlySellerWarehouses = true;
			filter.sellerWarehousesOnlyOrganizationId = this.sellerId;
		}
		if (search) {
			filter.search = search;
		}
		return filter;
	}

	@action.bound
	async handleOnWarehouseMenuScroll() {
		if (this.warehouseOptions.length < this.pageWarehouse * 15) return;
		let response = [];
		const newOptions = [];
		this.isWarehouseDropdownLoading = true;
		this.pageWarehouse++;
		if (!this.isBulkAction) {
			const productIds = getDistinctProductIds(this.selectedItem.fulfillmentOrderItems);
			response = await this.productInventoryStore.findProductInventory({
				rpp: 15,
				page: this.pageWarehouse,
				productIds: productIds,
				searchFields: 'assignedWarehouse.applicationTitle',
				calculateInventory: !this.isBulkAction,
				filterInapplicableWarehouses: !this.isBulkAction,
				includeOnlySellerWarehouses: true,
				sellerWarehousesOnlyOrganizationId: this.selectedItem.sellerId,
				embed: ['assignedWarehouse'],
				isOrganizationActive: true,
				useSafeQuantity: false,
			});
			_.forEach(response.item, item => {
				newOptions.push({ value: item.assignedWarehouseid, label: item.assignedWarehouse.applicationTitle });
			});
		} else {
			let filter = composeWarehouseFilter(this.pageWarehouse);
			response = await this.applicationStore.find(filter);
			_.forEach(response.item, item => {
				newOptions.push({ value: item.id, label: item.applicationTitle });
			});
		}
		if (newOptions.length > this.warehouseOptions.length) {
			this.warehouseOptions = [...newOptions];
		}
		this.isWarehouseDropdownLoading = false;
	}

	@action.bound
	async handleOnServiceCarrierMenuScroll() {
		if (this.carrierServiceOptions.length < this.pageCarrierService * 15) return;
		this.isCarrierServiceLoading = true;
		this.pageCarrierService++;
		await this.getCarrierServices(this.pageCarrierService, false, this.infiniteSearchPhrase);
		if (this.tempCarrierServiceOptions.length > this.carrierServiceOptions.length) {
			this.carrierServiceOptions = this.tempCarrierServiceOptions;
		}
	}

	@action.bound // Used when searching in dropdown (Select).
	async searchWarehouseAsync(searchPhrase, { action }) {
		if (searchPhrase === '') {
			this.pageWarehouse = 1;
		}
		if (action === 'input-change') {
			this.isWarehouseDropdownLoading = true;
			this.infiniteSearchPhrase = searchPhrase;
			this.pageWarehouse = 1;
			this.warehouseOptions = [];
			if (!this.isBulkAction) {
				const response = await this.productInventoryStore.findProductInventory({
					rpp: 15,
					page: this.page,
					search: this.searchPhrase,
					searchFields: 'assignedWarehouse.applicationTitle',
					fulfillmentOrderId: this.selectedItem.id,
					productIds: getDistinctProductIds(this.selectedItem.fulfillmentOrderItems),
					isOrganizationActive: true,
					useSafeQuantity: false,
					includeOnlySellerWarehouses: true,
					sellerWarehousesOnlyOrganizationId: this.selectedItem.sellerId,
					calculateInventory: !this.isBulkAction,
					filterInapplicableWarehouses: !this.isBulkAction,
					embed: ['assignedWarehouse'],
				});
				this.warehouseOptions = _.map(response.item, item => {
					return {
						value: item.assignedWarehouse.id,
						additionalinfo: this.selectedItem.fulfillmentOrderItems.length > 1 ? null : item.available,
						label: item.assignedWarehouse.applicationTitle,
						extra: item.assignedWarehouse,
					};
				});
			} else {
				let filter = this.composeWarehouseFilter(this.page, this.searchPhrase);
				const response = await this.applicationStore.find(filter);
				this.warehouseOptions = _.map(response.item, item => {
					return { value: item.id, label: item.applicationTitle, extra: item };
				});
			}
			this.isWarehouseDropdownLoading = false;
		}
	}

	@action.bound // Used when searching in dropdown (Select)
	async searchCarrierServiceAsync(searchPhrase, { action }) {
		if (searchPhrase === '') {
			this.pageCarrierService = 1;
		}
		if (action === 'input-change') {
			let newOptions = [];
			this.infiniteSearchPhrase = searchPhrase;
			this.isCarrierServiceLoading = true;
			if (!this.isBulkAction) {
				let warehouse = this.form.$('assignedWarehouse').value.extra;
				await this.rateServiceStore.findCarrierServiceWithRates({
					rateSearchRest: { searchQuery: searchPhrase || null, rpp: 30, page: 1 },
					fulfillmentOrderId: this.selectedItem.id,
					warehouseId: warehouse.id,
					channelId: this.selectedItem.order.channelId,
					isReturn: false,
					ignoreDeliverByDate: this.ignoreDeliverByDate,
				});
				newOptions = mapCarrierServiceWithRates(this.rateServiceStore.data);
			} else {
				await this.carrierServiceStore.findCarrierService({
					page: 1,
					rpp: 30,
					searchQuery: searchPhrase,
				});
				newOptions = _.map(this.carrierServiceStore.data, item => {
					return {
						value: item.id,
						label: item.name,
						extra: item,
					};
				});
			}
			newOptions = _.sortBy(newOptions, ['label']);
			this.carrierServiceOptions = newOptions;
			this.isCarrierServiceLoading = false;
		}
	}

	@action.bound
	async onMenuClose() {
		this.pageWarehouse = 1;
		this.infiniteSearchPhrase = null;
		await this.getWarehouseOptions(false);
	}

	@action.bound
	getWarehouseOptions = async (shouldResetSelectedItem = true) => {
		if (!this.isBulkAction) {
			const response = await this.productInventoryStore.findProductInventory({
				rpp: 15,
				page: this.page,
				search: this.searchPhrase,
				searchFields: 'assignedWarehouse.applicationTitle',
				fulfillmentOrderId: this.selectedItem.id,
				productIds: getDistinctProductIds(this.selectedItem.fulfillmentOrderItems),
				isOrganizationActive: true,
				useSafeQuantity: false,
				includeOnlySellerWarehouses: true,
				sellerWarehousesOnlyOrganizationId: this.selectedItem.sellerId,
				calculateInventory: !this.isBulkAction,
				filterInapplicableWarehouses: !this.isBulkAction,
				embed: ['assignedWarehouse'],
			});
			this.warehouseOptions = _.map(response.item, item => {
				return {
					label: item.assignedWarehouse.applicationTitle,
					additionalinfo: this.selectedItem.fulfillmentOrderItems.length > 1 ? null : item.available,
					value: item.assignedWarehouse.id,
					extra: item.assignedWarehouse,
				};
			});
		} else {
			let filter = this.composeWarehouseFilter(this.page, this.searchPhrase);
			const response = await this.applicationStore.find(filter);
			this.warehouseOptions = _.map(response.item, item => {
				return {
					label: item.applicationTitle,
					value: item.id,
					extra: item,
				};
			});
		}
		if (!this.isBulkAction && this.selectedItem.warehouseOrganization != null && shouldResetSelectedItem) {
			this.form.$('assignedWarehouse').value = {
				value: this.selectedItem.warehouseOrganization.id,
				label: this.selectedItem.warehouseOrganization.applicationTitle,
				extra: this.selectedItem.warehouseOrganization,
			};
		}
	};

	@action.bound
	getCarrierServices = async (page, shouldSuspend = true, searchPhrase = null, isInitialFetch = false) => {
		if (shouldSuspend) {
			this.suspend();
		}
		if (this.form.$('assignedWarehouse').value !== '') {
			var warehouse = this.form.$('assignedWarehouse').value.extra;
		}
		this.isCarrierServiceLoading = true;
		let options = [];
		if (this.isBulkAction) {
			options = await this.getCarrierServicesBulkAction();
		} else {
			if (
				this.form.$('useBuyShipping').value &&
				this.selectedItem.order.channel.channelType.name === channelType.amazon
			) {
				const shippingServiceParams = {
					fulfillmentOrderId: this.selectedItem.id,
					warehouseId: warehouse.id,
				};
				options = await this.getAmazonShippingServices(shippingServiceParams, options);
			} else {
				await this.rateServiceStore.findCarrierServiceWithRates({
					rateSearchRest: { search: searchPhrase, rpp: 30, page: page },
					fulfillmentOrderId: this.selectedItem.id,
					warehouseId: warehouse.id,
					channelId: this.selectedItem.order.channelId,
					ignoreDeliverByDate: this.isSuperAdminOrganization ? this.ignoreDeliverByDate : true,
				});
				options = mapCarrierServiceWithRates(this.rateServiceStore.data);
			}
			options = _.sortBy(options, ['label']);
		}

		if (!this.isBulkAction && this.form.$('assignedCarrierService').value != null) {
			const selectedCarrierService = _.find(
				options,
				item =>
					item.value.carrierServiceId === this.form.$('assignedCarrierService').value.value.carrierServiceId &&
					item.value.carrierId === this.form.$('assignedCarrierService').value.value.carrierId
			);
			if (selectedCarrierService != null) {
				this.form.$('assignedCarrierService').value = selectedCarrierService;
				this.disableSaveButton();
			} else {
				this.disableSaveBtn = true;
			}
		}
		this.tempCarrierServiceOptions = [...options];
		this.carrierServiceOptions = this.tempCarrierServiceOptions;
		this.isCarrierServiceLoading = false;
		if (shouldSuspend) {
			this.resume();
		}
		//If there are no eligible warehouses and carrier services beside those that are already assigned to order, disable save button and raise an error
		if (isInitialFetch && !this.isBulkAction) {
			this.checkInitialEligibleShippingData();
		}
	};

	getCarrierServicesBulkAction = async () => {
		await this.carrierServiceStore.findCarrierService({ page: 1, rpp: 50, embed: ['carrier'] });
		return _.map(this.carrierServiceStore.data, item => {
			return { value: item.id, label: item.name, extra: item };
		});
	};

	checkInitialEligibleShippingData() {
		if (
			(this.warehouseOptions.length == 0 ||
				(this.warehouseOptions.length == 1 &&
					this.warehouseOptions[0].value == this.selectedItem.assignedWarehouseId)) &&
			(this.carrierServiceOptions.length == 0 ||
				(this.carrierServiceOptions.length == 1 &&
					this.carrierServiceOptions[0].value.carrierServiceId == this.selectedItem.assignedCarrierServiceId &&
					this.carrierServiceOptions[0].value.carrierId == this.selectedItem.assignedCarrierId))
		) {
			this.rootStore.notificationStore.error('CHANGESHIPPINGVIEW.NOELIGIBLESHIPPINGDATA');
			this.disableSaveBtn = true;
		}
	}

	getAmazonShippingServices = async (requestBody, options) => {
		await this.rateServiceStore.getAmazonShippingServices(requestBody);
		if (this.rateServiceStore.response.error) {
			const errorMessage = this.rateServiceStore.response.error;
			this.rootStore.notificationStore.error([errorMessage, { name: this.selectedItem.order.channel.name }]);
			return (options = []);
		} else {
			options = mapCarrierServiceWithRates(this.rateServiceStore.response.carrierServices, true);
			options = _.sortBy(options, ['label']);
			return options;
		}
	};

	@action.bound async changeShipping(voidOption) {
		this.suspend();
		try {
			const { assignedWarehouse, assignedCarrierService, useBuyShipping } = this.form.values();
			this.selectedItem.assignedWarehouseId = assignedWarehouse.value;
			this.selectedItem.assignedCarrierServiceId = assignedCarrierService.value.carrierServiceId;
			this.selectedItem.assignedRate =
				assignedCarrierService.extra.carrier != null &&
				(assignedCarrierService.extra.carrier.isThirdParty || assignedCarrierService.extra.carrier.isPartialThirdParty)
					? 0
					: assignedCarrierService.extra.rate;
			this.selectedItem.assignedRateSurcharge =
				assignedCarrierService.extra.carrier != null
					? isSome(assignedCarrierService.extra.carrier.surplusCharge)
						? assignedCarrierService.extra.carrier.isThirdParty ||
						  assignedCarrierService.extra.carrier.isPartialThirdParty
							? 0 // carrier has surcharge but it is third party, so it's saved as 0
							: assignedCarrierService.extra.rateComposition.surcharge // carrier has surcharge and its not third party
						: null // carrier has no surcharge
					: null;
			this.selectedItem.assignedCarrierId =
				assignedCarrierService.value.carrierId != null ? assignedCarrierService.value.carrierId : null;
			this.selectedItem.useBuyShipping = useBuyShipping;
			const changeShippingParams = {
				id: this.selectedItem.id,
				fulfillmentOrder: this.selectedItem,
				useBuyShipping: useBuyShipping,
				voidOption: voidOption,
			};
			await this.fulfillmentOrderStore.changeShipping(
				changeShippingParams,
				`${sourceOfChange.changeShippingOrder} - ${sourceOfChange.voidLabelOption[voidOption]}`
			);
			this.rootStore.notificationStore.success([
				'CHANGESHIPPINGVIEW.SUCCESS',
				{ id: this.selectedItem.fulfillmentOrderId },
			]);
			if (voidOption !== 3) {
				if (this.parentView.resetPrintingData) {
					this.parentView.resetPrintingData();
				}
			}
			this.parentView.changeShippingModal.close();
			if (this.parentView.queryUtility) {
				this.parentView.queryUtility.initialize(false);
			} else {
				this.parentView.initialize();
			}
		} catch (err) {
			this.rootStore.notificationStore.error(['CHANGESHIPPINGVIEW.FAIL', { id: this.selectedItem.fulfillmentOrderId }]);
		}
		this.resume();
	}

	@action.bound async changeShippingBulk(voidOption) {
		try {
			const { assignedWarehouse, assignedCarrierService, useBuyShipping, ignoreDeliverByDate } = this.form.values();
			const selectedItems = [];
			const notProcessableItems = [];
			this.selectedItems.map(selectedItem => {
				let index = _.findIndex(onChangeShippingStatuses, item => item == selectedItem.fulfillmentOrderStatus.abrv);
				if (index != -1) {
					if (assignedCarrierService != null) {
						selectedItem.assignedCarrierServiceId = assignedCarrierService.value;
						selectedItem.carrierService = null;
					}
					if (assignedWarehouse != null) {
						selectedItem.assignedWarehouseId = assignedWarehouse.value;
						selectedItem.warehouseOrganization = null;
					}
					if (selectedItem.order.channel.channelType.name === channelType.amazonVendor) {
						notProcessableItems.push(selectedItem);
					} else {
						selectedItems.push(selectedItem);
					}
				} else {
					notProcessableItems.push(selectedItem);
				}
			});
			if (selectedItems.length > 0) {
				this.voidActiveLabelConfirmModal.close();
				this.parentView.changeShippingModalBulk.close();
				this.parentView.isSelectAllCheckboxSelected = false;
				this.resume();
				this.parentView.selectedItems.forEach(o => delete o.isChecked);
				this.parentView.selectedItems = [];
				if (this.parentView.selectedGroupByOption) {
					this.parentView.selectedGroups.forEach(g => {
						delete g.isChecked;
					});
					this.parentView.selectedGroups = [];
				}
				this.parentView.selectedGroups = [];
				const changeShippingBulkParams = {
					fulfillmentOrders: selectedItems,
					useBuyShipping: useBuyShipping,
					voidOption: voidOption,
					ignoreDeliverByDate: ignoreDeliverByDate,
				};
				const notificationId = this.rootStore.notificationStore.load(translate('BULKNOTIFICATION.CHANGESHIPPING'));
				const count = await this.fulfillmentOrderStore.changeShippingBulk(
					changeShippingBulkParams,
					`${sourceOfChange.changeShippingOrder} - ${sourceOfChange.bulk} - ${sourceOfChange.voidLabelOption[voidOption]}`
				);
				this.rootStore.notificationStore.dismiss(notificationId);
				if (count > 0) {
					if (count == selectedItems.length) {
						this.rootStore.notificationStore.success([
							'CHANGESHIPPINGVIEW.SUCCESSBULK',
							{
								count: count,
								countMax: selectedItems.length,
								notProcessable: notProcessableItems.length,
							},
						]);
					} else {
						this.rootStore.notificationStore.success([
							'CHANGESHIPPINGVIEW.PARTIALSUCCESSBULK',
							{
								count: count,
								countMax: selectedItems.length,
								notProcessable: notProcessableItems.length,
							},
						]);
					}
					this.parentView.findFulfillmentOrders();
				} else {
					this.rootStore.notificationStore.error('CHANGESHIPPINGVIEW.NOVALIDORDERSELECTED');
				}
			} else {
				this.parentView.changeShippingModalBulk.close();
				this.rootStore.notificationStore.error('CHANGESHIPPINGVIEW.NOVALIDORDERSELECTED');
			}
			if (voidOption !== 3) {
				if (this.parentView.resetPrintingData) {
					this.parentView.resetPrintingData();
				}
			}
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
	}

	@action.bound
	openVoidActiveLabelConfirmModal() {
		if (this.isBulkAction) {
			for (const selectedItem of this.selectedItems) {
				const activeShipment = _.find(selectedItem.shipmentInfos, item => {
					return item.shipmentInfoStatus.abrv == 'active';
				});
				if (activeShipment != null) {
					var hasActiveShipments = true;
					var hasActiveShipmentsWithBuyShipping = activeShipment.isBuyShipping == true;
					if (hasActiveShipmentsWithBuyShipping == true) break;
				}
			}
		}
		if (this.activeShipment != null || hasActiveShipments) {
			const message =
				hasActiveShipmentsWithBuyShipping || (!this.isBulkAction && this.activeShipment.isBuyShipping)
					? this.isBulkAction
						? { message: 'CHANGECARRIERVIEW.MODAL.BULKVOIDLABEL' }
						: {
								message: [
									'CHANGECARRIERVIEW.MODAL.VOIDLABEL',
									{ fulfillmentOrderId: this.selectedItem.fulfillmentOrderId },
								],
						  }
					: null;
			if (message == null) {
				this.isBulkAction ? this.changeShippingBulk(0) : this.changeShipping(0);
			} else {
				this.voidActiveLabelConfirmModal.open({
					...message,
				});
			}
		} else {
			if (this.selectedItems != null) {
				this.changeShippingBulk(3);
			} else {
				this.changeShipping(3);
			}
		}
	}

	@action.bound
	onWarehouseSelect = async item => {
		this.suspend();
		this.form.$('assignedWarehouse').value = item;
		this.pageCarrierService = 1;
		this.tempCarrierServiceOptions = [];
		if (this.useBuyShipping) {
			this.form.$('assignedCarrierService').value = null;
			this.disableSaveBtn = true;
		}
		if (!this.isBulkAction) {
			if (item.value != this.selectedItem.warehouseId && isSome(this.form.$('assignedCarrierService').value)) {
				this.disableSaveBtn = false;
			} else {
				this.disableSaveBtn = true;
			}
		} else {
			this.disableSaveBtn = false;
		}
		if (!this.isBulkAction) {
			await this.getWarehouseOptions(false);
			await this.getCarrierServices(this.pageCarrierService, false);
		}
		this.resume();
	};

	@action.bound
	onCarrierServiceSelect = carrierService => {
		this.form.$('assignedCarrierService').value = carrierService;
		if (carrierService == null) {
			this.disableSaveBtn = true;
		}
		if (!this.isBulkAction) {
			this.disableSaveButton();
		} else {
			this.disableSaveBtn = false;
		}
	};

	disableSaveButton = () => {
		if (this.shippingDataChanged()) {
			this.disableSaveBtn = false;
		} else {
			this.disableSaveBtn = true;
		}
	};

	shippingDataChanged = () => {
		return (
			this.form.$('assignedWarehouse').value.value != this.selectedItem.assignedWarehouseId ||
			(this.form.$('assignedCarrierService').value.value.carrierServiceId !=
				this.selectedItem.assignedCarrierServiceId ||
				this.form.$('assignedCarrierService').value.value.carrierId != this.selectedItem.assignedCarrierId ||
				this.form.$('useBuyShipping').value != this.selectedItem.useBuyShipping)
		);
	};

	@action.bound
	onCheck = async event => {
		this.form.$('useBuyShipping').value = event.currentTarget.checked;
		this.form.$('assignedCarrierService').set('value', null);
		this.disableSaveBtn = true;
		this.debouncedOnCheck();
	};

	@action.bound
	onIgnoreDeliverByDateChange = async value => {
		this.ignoreDeliverByDate = value;
		if (!this.isBulkAction) {
			if (this.selectedItem.order.channel.channelType.name === channelType.amazon) {
				this.form.$('useBuyShipping').value = !this.ignoreDeliverByDate;
			}

			if (this.form.$('assignedWarehouse').value) {
				this.form.$('assignedCarrierService').value = null;
				await this.getCarrierServices(this.pageCarrierService);
			}
		}

		if (this.isBulkAction) {
			this.form.$('useBuyShipping').value = !this.ignoreDeliverByDate;
			this.useBuyShipping = !this.ignoreDeliverByDate;
		}
	};
}

export default ChangeShippingViewStore;



// WEBPACK FOOTER //
// ./src/modules/order/stores/ChangeShippingViewStore.js